import { create } from 'zustand';

/**
 * Notification Store
 *
 * Lightweight transient notifications (toasts) for surfacing failures
 * the UI would otherwise silently swallow. Domain-specific bark/psyche
 * overlays already exist; this is the catch-all for system signals
 * (failed writes, dice not ready, etc.).
 *
 * Use sparingly — toasts are interruption-shaped. Reserve for things
 * the user genuinely cannot ignore (lost data, retry needed).
 */

export type NotificationLevel = 'info' | 'warning' | 'error';

export interface Notification {
  id: string;
  level: NotificationLevel;
  message: string;
  /** Optional supplementary line (e.g. retry hint) */
  detail?: string;
  /** Optional action button label + handler */
  action?: { label: string; onClick: () => void };
  /** ms before auto-dismiss; 0 = sticky (user must dismiss). Default 6000. */
  ttl: number;
  createdAt: number;
}

interface NotifyParams {
  level?: NotificationLevel;
  message: string;
  detail?: string;
  action?: Notification['action'];
  ttl?: number;
}

interface NotificationState {
  notifications: Notification[];
  push: (params: NotifyParams) => string;
  dismiss: (id: string) => void;
  clearAll: () => void;
}

const DEFAULT_TTL_MS = 6000;
const MAX_VISIBLE = 5;

const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],

  push: ({ level = 'info', message, detail, action, ttl = DEFAULT_TTL_MS }) => {
    const id = `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const notification: Notification = {
      id,
      level,
      message,
      detail,
      action,
      ttl,
      createdAt: Date.now(),
    };

    set((state) => {
      const next = [...state.notifications, notification];
      // Trim oldest if we exceed max — favor recent failures over stale ones.
      while (next.length > MAX_VISIBLE) {
        const dropped = next.shift();
        if (dropped) {
          const t = dismissTimers.get(dropped.id);
          if (t) {
            clearTimeout(t);
            dismissTimers.delete(dropped.id);
          }
        }
      }
      return { notifications: next };
    });

    if (ttl > 0) {
      const timer = setTimeout(() => {
        get().dismiss(id);
      }, ttl);
      dismissTimers.set(id, timer);
    }

    return id;
  },

  dismiss: (id) => {
    const timer = dismissTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      dismissTimers.delete(id);
    }
    set((state) => ({
      notifications: state.notifications.filter((n) => n.id !== id),
    }));
  },

  clearAll: () => {
    for (const timer of dismissTimers.values()) clearTimeout(timer);
    dismissTimers.clear();
    set({ notifications: [] });
  },
}));

/** Convenience helper — outside-component callsites can push without hooking. */
export function notify(params: NotifyParams): string {
  return useNotificationStore.getState().push(params);
}
